import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate.ts";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { canViewSharedEmails, getChargebackEmailsRequest, getSharedEmailsRequest, parseChargebackEmails, parseSharedEmails, type IChargebackEmailsResult, type ISharedEmail, type ISharedEmailsFilters, type TEmailListKind } from "./sharedEmails";

export interface ISharedEmailsScope {
	userId: number;
	projectId: number;
	filters: Pick<ISharedEmailsFilters, "from" | "to" | "currency" | "client_id">;
}

interface ISharedEmailsState {
	key: string;
	busy: boolean;
	rows: ISharedEmail[] | null;
	chargebacks: IChargebackEmailsResult | null;
	total?: number;
	truncated?: boolean;
	error: boolean;
	denied: boolean;
}

export function useSharedEmails({ userId, projectId, filters }: ISharedEmailsScope, enabled = false, kind: TEmailListKind = "shared") {
	useLocale();
	const { from, to, currency, client_id } = kind === "shared" ? filters : { from: "", to: "", currency: undefined, client_id: undefined };
	const key = JSON.stringify([userId, projectId, kind, from, to, currency, client_id]);
	const [attempt, setAttempt] = useState(0);
	const [state, setState] = useState<ISharedEmailsState>({ key, busy: enabled, rows: null, chargebacks: null, error: false, denied: false });

	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;
		const controller = new AbortController();
		let deadline: ReturnType<typeof setTimeout> | undefined;
		const empty = { key, busy: false, rows: null, chargebacks: null, error: false, denied: false };
		const subscription = GD.S_AUTH_LOG_OUT.subscribe(() => {
			cancelled = true;
			controller.abort();
			clearTimeout(deadline);
			setState({ ...empty, denied: true });
		});
		setState({ ...empty, busy: true });
		const checkAccess = async () => {
			const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
			if (cancelled || controller.signal.aborted) return false;
			if (profile?.id !== userId || project?.id !== projectId || !canViewSharedEmails(profile, projectId)) {
				setState({ ...empty, denied: true });
				return false;
			}
			return true;
		};
		const timer = setTimeout(() => {
			if (cancelled) return;
			deadline = setTimeout(() => {
				controller.abort();
				if (!cancelled) setState({ ...empty, error: true });
			}, 45_000);
			void (async () => {
				try {
					if (!await checkAccess()) return;
					const response = await GD.REQ_API.request({
						...(kind === "shared" ? getSharedEmailsRequest({ from, to, currency, client_id }, projectId) : getChargebackEmailsRequest(projectId)),
						signal: controller.signal,
					});
					if (!await checkAccess()) return;
					if (response?.error) throw new Error(msg("sharedEmailsRequestFailed"));
					const result = kind === "shared" ? parseSharedEmails(response?.data) : null;
					const chargebacks = kind === "chargebacks" ? parseChargebackEmails(response?.data) : null;
					setState({ ...empty, rows: result?.rows ?? null, chargebacks,
						total: result?.total ?? chargebacks?.total, truncated: result?.truncated ?? chargebacks?.truncated });
				} catch {
					if (!cancelled && !controller.signal.aborted) setState({ ...empty, error: true });
				} finally { clearTimeout(deadline); }
			})();
		}, 250);
		return () => {
			cancelled = true;
			controller.abort();
			clearTimeout(timer);
			clearTimeout(deadline);
			GD.S_AUTH_LOG_OUT.unsubscribe(subscription);
		};
	}, [key, userId, projectId, kind, from, to, currency, client_id, attempt, enabled]);

	return {
		...(state.key === key ? state : { key, busy: enabled, rows: null, chargebacks: null, error: false, denied: false }),
		busy: enabled && (state.key !== key || state.busy),
		retry: () => setAttempt(value => value + 1),
	};
}